import { t } from '@lingui/core/macro';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useCopyMediaManagerTemplateMutation } from 'modules/manager/store/media-manager-api';
import { useCallback, useRef, useState } from 'react';
import { i18n } from 'shared/i18n';

export interface DuplicateTemplateOptions {
  /** Name for the copy. Blank/omitted falls back to `<source> (копия)`. */
  name?: string;
  templateGroupId: number;
}

export interface UseDuplicateTemplateResult {
  isDuplicating: boolean;
  error: string | null;
  duplicate: (templateId: number, options: DuplicateTemplateOptions) => Promise<MediaManagerTemplate | null>;
  resetError: () => void;
}

export function useDuplicateTemplate(): UseDuplicateTemplateResult {
  const [copyTemplate] = useCopyMediaManagerTemplateMutation();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const duplicate = useCallback(
    async (templateId: number, options: DuplicateTemplateOptions): Promise<MediaManagerTemplate | null> => {
      if (inFlight.current) return null;

      inFlight.current = true;
      setIsDuplicating(true);
      setError(null);
      try {
        const name = options.name?.trim();
        const copy = await copyTemplate({
          templateId,
          templateGroupId: options.templateGroupId,
          name: name || undefined,
        }).unwrap();
        return copy;
      } catch (err) {
        console.error('Failed to duplicate media manager template:', err);
        setError(
          isTemplateNameConflict(err) ? i18n._(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось создать копию, попробуйте ещё раз`,
        );
        return null;
      } finally {
        inFlight.current = false;
        setIsDuplicating(false);
      }
    },
    [copyTemplate],
  );

  const resetError = useCallback(() => setError(null), []);

  return { isDuplicating, error, duplicate, resetError };
}
